import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import Router from '@ember/routing/router';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import logger from 'donor-elf-web/lib/logger';
import FileUploadInfoModel from 'donor-elf-web/models/file-upload-info';
import type { MpdxImportRouteModel } from 'donor-elf-web/routes/funds/fund/import/mpdx';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import FundsService from 'donor-elf-web/services/funds-service';
import Settings from 'donor-elf-web/services/settings';

export default class MpdxImportController extends Controller {
  model!: MpdxImportRouteModel;

  @service declare contactsCache: ContactsCache;
  @service declare fundsService: FundsService;
  @service declare settings: Settings;
  @service declare store: Store;
  @service declare router: Router;

  file?: File;
  @tracked invalidFileName = false;
  @tracked fileUploading = false;
  @tracked fileUploaded = false;
  @tracked fileUploadInfo?: FileUploadInfoModel;
  @tracked fileUploadProgressPercent: number = 0;
  @tracked fileUploadProgressBackgroundClass: string = 'bg-brand';
  @tracked importingFile = false;
  @tracked importComplete = false;
  @tracked errorMessage?: string;
  @tracked error: any;

  get showAlertAboutPossibleDuplicateContacts() {
    return this.contactsCache.contacts.length > 0;
  }

  get fileUploadComplete() {
    return this.model.alreadyImporting || this.fileUploaded;
  }

  get importing() {
    return this.model.alreadyImporting || this.importingFile;
  }

  // # example: 1A3456-2017-04-01-April-receipts.csv
  get validFileName() {
    return this.file!.name.match(/\.xml$/i);
  }

  @action
  templateWillDestroy() {
    this.file = undefined;
    this.invalidFileName = false;
    this.fileUploading = false;
    this.fileUploaded = false;
    this.fileUploadInfo = undefined;
    this.fileUploadProgressPercent = 0;
    this.fileUploadProgressBackgroundClass = 'bg-brand';
    this.importingFile = false;
    this.importComplete = false;
  }

  @action
  handleFileInputChanged(e: any) {
    if (e.target.files.length === 1) {
      this.file = e.target.files[0];
      if (this.validFileName) {
        this.invalidFileName = false;
        this.upload();
      } else {
        this.invalidFileName = true;
        // this.errorMessage = "Invalid file name. The file should end with '.xml'";
      }
    } else {
      this.file = undefined;
    }
  }

  @action
  async upload() {
    if (this.fileUploading || this.fileUploaded) {
      return;
    }
    this.errorMessage = undefined;
    if (!this.file) {
      this.errorMessage = 'Select a file first';
      return;
    }

    this.fileUploadInfo = await this.store.queryRecord('file-upload-info', {
      filter: { file_name: this.file.name },
    });
    this.fileUploadProgressPercent = 5;
    this.fileUploadProgressBackgroundClass = 'bg-brand';
    this.fileUploading = true;
  }

  @action
  handleFileUploadProgress(percentUploaded: number) {
    this.fileUploadProgressPercent = Math.max(percentUploaded, 5);
  }

  @action
  async handleFileUploadComplete() {
    this.fileUploadProgressPercent = 100;
    this.fileUploaded = true;
    this.model.mpdxImport!.s3Key = this.fileUploadInfo!.key;
    setTimeout(() => {
      // delaying changing background color until the width transition completes
      this.fileUploadProgressBackgroundClass = 'bg-success';
    }, 500);
  }

  @action
  handleFileUploadError(error: any) {
    logger.error(error);
    this.errorMessage = 'There was an error uploading the file';
  }

  @action
  async import() {
    this.importingFile = true;
    try {
      await this.model.mpdxImport!.save();
    } catch (error) {
      if (error.errors) {
        this.errorMessage = error.errors[0].detail;
      }
      logger.error(error);
    }
  }

  @action
  async handleFundUploadComplete() {
    this.settings.unload();
    await this.settings.loadSettings(this.fundsService.selectedFund!.id);
    await this.contactsCache.loadModifiedContacts();
    this.importingFile = false;
    this.importComplete = true;
  }

  @action
  handleFundUploadError() {
    this.errorMessage =
      "Sorry! There was an error importing your data from MPDx. Please send us an email at support@donorelf.com to let us know there was an error, and we'll get it fixed for you.";
  }
}
